import { c as _c } from "react/compiler-runtime";
import { Menu, menuItemVariants, Popover } from '@systemeio/components';
import { ChevronDownIcon, User01Icon } from '@systemeio/icons';
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch';
import { SelectorField, SelectorItem, selectorPopupClassName, selectorTriggerClassName } from 'shared/components/navbar/components/selector-item';
import { SubaccountsPageLink } from 'shared/components/navbar/components/subaccounts-page-link';
import { MOBILE_NAVBAR_ITEM_CLASS_NAME } from 'shared/components/navbar/constants';
import { pageRoute } from 'shared/constants/routes.constants';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { usePermission } from 'shared/hooks/use-permission';
import { useRouter } from 'shared/hooks/use-router';
import useUser from 'shared/hooks/use-user';
import { generatePretendUserUrl } from 'shared/hooks/use-user/utils/generate-pretend-user-url';
import { hasSubAccounts } from 'shared/hooks/use-user/utils/has-subaccounts';
import { getPageRoute } from 'shared/utils/get-dynamic-page-route';
import { twMerge } from 'tailwind-merge';
interface SubaccountSelectorProps {
  isDesktop: boolean;
}
export function SubaccountSelector(t0) {
  const $ = _c(54);
  const {
    isDesktop
  } = t0;
  const {
    dashboardUser,
    isDisabled,
    isSupportImpersonation,
    isWorkspaceOwner
  } = useUser();
  const {
    pagePermissions
  } = usePermission();
  const {
    pathname,
    push
  } = useRouter();
  const {
    t
  } = useLocoTranslation();
  if (!dashboardUser || isDisabled || !isWorkspaceOwner) {
    return null;
  }
  let t1;
  if ($[0] !== t) {
    t1 = t("header.user.profile.menu.sub_accounts");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const label = t1;
  const canOpenSubaccountsPage = Boolean(pagePermissions[pageRoute.Subaccounts]);
  if (!hasSubAccounts(dashboardUser) || !dashboardUser.mainUser) {
    if (!canOpenSubaccountsPage) {
      return null;
    }
    let t2;
    if ($[2] !== isDesktop || $[3] !== label || $[4] !== pathname) {
      const isActive = pathname.includes(pageRoute.Subaccounts);
      const t3 = isActive ? "page" as const : undefined;
      let t4;
      if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = getPageRoute(pageRoute.Subaccounts);
        $[6] = t4;
      } else {
        t4 = $[6];
      }
      let t5;
      if ($[7] !== t3) {
        t5 = {
          "aria-current": t3,
          href: t4
        };
        $[7] = t3;
        $[8] = t5;
      } else {
        t5 = $[8];
      }
      const linkProps = t5;
      let t6;
      if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = <User01Icon />;
        $[9] = t6;
      } else {
        t6 = $[9];
      }
      let t7;
      if ($[10] !== label) {
        t7 = <>{t6}<span className="min-w-0 truncate">{label}</span></>;
        $[10] = label;
        $[11] = t7;
      } else {
        t7 = $[11];
      }
      const content = t7;
      t2 = isDesktop ? <Popover.Close className={twMerge(menuItemVariants({
        state: isActive ? "active" : "default"
      }), "gap-1.5")} render={<LinkWithoutPrefetch {...linkProps} />}>{content}</Popover.Close> : <LinkWithoutPrefetch {...linkProps} className={twMerge(MOBILE_NAVBAR_ITEM_CLASS_NAME, isActive && "text-text-link [&_svg]:text-icon-accent")}>{content}</LinkWithoutPrefetch>;
      $[2] = isDesktop;
      $[3] = label;
      $[4] = pathname;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    return t2;
  }
  const t2 = dashboardUser.subaccountsAvatarUrls[dashboardUser.mainUser.user.id] ?? null;
  let t3;
  if ($[12] !== dashboardUser.mainUser.displayName || $[13] !== dashboardUser.mainUser.user.id || $[14] !== t2) {
    t3 = {
      id: dashboardUser.mainUser.user.id,
      displayName: dashboardUser.mainUser.displayName,
      image: t2
    };
    $[12] = dashboardUser.mainUser.displayName;
    $[13] = dashboardUser.mainUser.user.id;
    $[14] = t2;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  const mainAccount = t3;
  let t4;
  if ($[16] !== dashboardUser.subaccounts || $[17] !== dashboardUser.subaccountsAvatarUrls || $[18] !== mainAccount) {
    let t5;
    if ($[20] !== dashboardUser.subaccountsAvatarUrls) {
      t5 = t6 => {
        const {
          user: t7,
          displayName
        } = t6;
        const {
          id
        } = t7;
        return {
          id,
          displayName,
          image: dashboardUser.subaccountsAvatarUrls[id] ?? null
        };
      };
      $[20] = dashboardUser.subaccountsAvatarUrls;
      $[21] = t5;
    } else {
      t5 = $[21];
    }
    t4 = [mainAccount, ...dashboardUser.subaccounts.map(t5)];
    $[16] = dashboardUser.subaccounts;
    $[17] = dashboardUser.subaccountsAvatarUrls;
    $[18] = mainAccount;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  const subaccountsData = t4;
  let t5;
  if ($[22] !== dashboardUser.id || $[23] !== mainAccount || $[24] !== subaccountsData) {
    t5 = subaccountsData.find(subAccount => subAccount.id === dashboardUser.id) ?? mainAccount;
    $[22] = dashboardUser.id;
    $[23] = mainAccount;
    $[24] = subaccountsData;
    $[25] = t5;
  } else {
    t5 = $[25];
  }
  const selectedSubaccount = t5;
  let t6;
  if ($[26] !== dashboardUser.id || $[27] !== dashboardUser.mainUser.user.id || $[28] !== isSupportImpersonation || $[29] !== push) {
    t6 = id_0 => {
      if (id_0 === dashboardUser.id) {
        return;
      }
      const isImpersonateToMainAccount = id_0 === dashboardUser.mainUser?.user.id;
      push(generatePretendUserUrl(isImpersonateToMainAccount && !isSupportImpersonation ? "_exit" : id_0));
    };
    $[26] = dashboardUser.id;
    $[27] = dashboardUser.mainUser.user.id;
    $[28] = isSupportImpersonation;
    $[29] = push;
    $[30] = t6;
  } else {
    t6 = $[30];
  }
  const changeSubAccount = t6;
  let t7;
  if ($[31] !== canOpenSubaccountsPage) {
    t7 = canOpenSubaccountsPage && <SubaccountsPageLink />;
    $[31] = canOpenSubaccountsPage;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  let t8;
  if ($[33] !== t) {
    t8 = t("global.switch_subaccounts");
    $[33] = t;
    $[34] = t8;
  } else {
    t8 = $[34];
  }
  let t9;
  if ($[35] !== selectedSubaccount.displayName || $[36] !== selectedSubaccount.image) {
    t9 = () => <><SelectorItem src={selectedSubaccount.image} displayName={selectedSubaccount.displayName} /><ChevronDownIcon className="transition-base text-field-icon size-5 shrink-0" /></>;
    $[35] = selectedSubaccount.displayName;
    $[36] = selectedSubaccount.image;
    $[37] = t9;
  } else {
    t9 = $[37];
  }
  let t10;
  if ($[38] !== label) {
    t10 = {
      "aria-label": label,
      className: selectorTriggerClassName,
      "data-test-id": "subaccount-selector-button"
    };
    $[38] = label;
    $[39] = t10;
  } else {
    t10 = $[39];
  }
  let t11;
  if ($[40] !== changeSubAccount || $[41] !== selectedSubaccount.id || $[42] !== subaccountsData) {
    t11 = subaccountsData.map(subaccount => ({
      id: String(subaccount.id),
      state: subaccount.id === selectedSubaccount.id ? "active" : "default",
      label: <SelectorItem isOption={true} displayName={subaccount.displayName} src={subaccount.image} />,
      onSelect: () => changeSubAccount(subaccount.id)
    }));
    $[40] = changeSubAccount;
    $[41] = selectedSubaccount.id;
    $[42] = subaccountsData;
    $[43] = t11;
  } else {
    t11 = $[43];
  }
  let t12;
  if ($[44] !== t10 || $[45] !== t11 || $[46] !== t8 || $[47] !== t9) {
    t12 = <Menu drawerTitle={t8} className={selectorPopupClassName} trigger={t9} triggerProps={t10} items={t11} />;
    $[44] = t10;
    $[45] = t11;
    $[46] = t8;
    $[47] = t9;
    $[48] = t12;
  } else {
    t12 = $[48];
  }
  let t13;
  if ($[49] !== isDesktop || $[50] !== label || $[51] !== t12 || $[52] !== t7) {
    t13 = <SelectorField action={t7} isDesktop={isDesktop} label={label}>{t12}</SelectorField>;
    $[49] = isDesktop;
    $[50] = label;
    $[51] = t12;
    $[52] = t7;
    $[53] = t13;
  } else {
    t13 = $[53];
  }
  return t13;
}